import { useRef, useCallback, type ReactNode } from "react";
import { Send, Trash2 } from "lucide-react";

interface SwipeableCardProps {
	children: ReactNode;
	onSwipeRight?: () => void;
	onSwipeLeft?: () => void;
	disabled?: boolean;
	/** Called before the swipe-right flyOut plays. Return false to snap back instead. */
	canSwipeRight?: () => boolean;
}

const THRESHOLD = 0.3; // 30% of card width to trigger
const ANGLE_LOCK = 30; // degrees — if initial angle is steeper than this, abort swipe (let scroll happen)
const FLYOUT_MS = 200;
const COLLAPSE_MS = 250;

export function SwipeableCard({ children, onSwipeRight, onSwipeLeft, disabled, canSwipeRight }: SwipeableCardProps) {
	const containerRef = useRef<HTMLDivElement>(null);
	const cardRef = useRef<HTMLDivElement>(null);
	const leftBgRef = useRef<HTMLDivElement>(null);
	const rightBgRef = useRef<HTMLDivElement>(null);
	const leftIconRef = useRef<HTMLDivElement>(null);
	const rightIconRef = useRef<HTMLDivElement>(null);

	const startX = useRef(0);
	const startY = useRef(0);
	const deltaX = useRef(0);
	const locked = useRef<"horizontal" | "vertical" | null>(null);
	const swiping = useRef(false);

	const resetCard = useCallback(() => {
		const card = cardRef.current;
		if (!card) return;
		card.style.transition = "transform 250ms ease-out";
		card.style.transform = "translateX(0)";
		if (leftBgRef.current) leftBgRef.current.style.opacity = "0";
		if (rightBgRef.current) rightBgRef.current.style.opacity = "0";
		if (leftIconRef.current) { leftIconRef.current.style.transform = "translateX(0)"; leftIconRef.current.style.opacity = "0"; }
		if (rightIconRef.current) { rightIconRef.current.style.transform = "translateX(0)"; rightIconRef.current.style.opacity = "0"; }
		setTimeout(() => {
			if (card) card.style.transition = "";
		}, 260);
	}, []);

	const flyOut = useCallback((direction: "left" | "right", callback?: () => void) => {
		const card = cardRef.current;
		const container = containerRef.current;
		if (!card || !container) return;

		const width = container.offsetWidth;
		const target = direction === "right" ? width : -width;

		card.style.transition = `transform ${FLYOUT_MS}ms ease-in`;
		card.style.transform = `translateX(${target}px)`;

		setTimeout(() => {
			// Collapse the row
			container.style.transition = `height ${COLLAPSE_MS}ms ease-out, opacity ${COLLAPSE_MS}ms ease-out`;
			container.style.height = `${container.offsetHeight}px`;
			// Force reflow
			container.offsetHeight;
			container.style.height = "0px";
			container.style.opacity = "0";
			container.style.overflow = "hidden";

			setTimeout(() => {
				callback?.();
			}, COLLAPSE_MS);
		}, FLYOUT_MS);
	}, []);

	const handleTouchStart = useCallback((e: React.TouchEvent) => {
		if (disabled) return;
		const touch = e.touches[0];
		startX.current = touch.clientX;
		startY.current = touch.clientY;
		deltaX.current = 0;
		locked.current = null;
		swiping.current = false;
	}, [disabled]);

	const handleMouseDown = useCallback((e: React.MouseEvent) => {
		if (disabled) return;
		startX.current = e.clientX;
		startY.current = e.clientY;
		deltaX.current = 0;
		locked.current = null;
		swiping.current = true;

		const handleMouseMove = (ev: MouseEvent) => {
			handleMove(ev.clientX, ev.clientY);
		};
		const handleMouseUp = () => {
			document.removeEventListener("mousemove", handleMouseMove);
			document.removeEventListener("mouseup", handleMouseUp);
			swiping.current = false;
			handleEnd();
		};
		document.addEventListener("mousemove", handleMouseMove);
		document.addEventListener("mouseup", handleMouseUp);
	}, [disabled]); // eslint-disable-line react-hooks/exhaustive-deps

	const handleMove = useCallback((clientX: number, clientY: number) => {
		const card = cardRef.current;
		const container = containerRef.current;
		if (!card || !container) return;

		const dx = clientX - startX.current;
		const dy = clientY - startY.current;

		// Lock direction on first significant move
		if (locked.current === null && (Math.abs(dx) > 5 || Math.abs(dy) > 5)) {
			const angle = Math.abs(Math.atan2(dy, dx) * (180 / Math.PI));
			// If angle is too vertical (close to 90deg), let scroll handle it
			if (angle > (90 - ANGLE_LOCK) && angle < (90 + ANGLE_LOCK)) {
				locked.current = "vertical";
				return;
			}
			locked.current = "horizontal";
		}

		if (locked.current !== "horizontal") return;

		deltaX.current = dx;

		// Don't allow swipe in a direction that has no handler
		if (dx > 0 && !onSwipeRight) return;
		if (dx < 0 && !onSwipeLeft) return;

		card.style.transform = `translateX(${dx}px)`;

		const width = container.offsetWidth;

		const absDx = Math.abs(dx);

		const halfWidth = width / 2;

		const ICON_OFFSET = 56; // px of drag before icon appears

		if (dx > 0) {
			// Swiping right — export
			if (leftBgRef.current) leftBgRef.current.style.opacity = "1";
			if (rightBgRef.current) rightBgRef.current.style.opacity = "0";
			if (leftIconRef.current) {
				// Icon travels with the card edge until it reaches the center, then stays
				const iconPos = Math.min(absDx - ICON_OFFSET, halfWidth);
				leftIconRef.current.style.transform = `translateX(${Math.max(0, iconPos)}px)`;
				leftIconRef.current.style.opacity = absDx > ICON_OFFSET ? "1" : "0";
				leftIconRef.current.style.transition = "opacity 150ms ease";
			}
		} else {
			// Swiping left — delete
			if (rightBgRef.current) rightBgRef.current.style.opacity = "1";
			if (leftBgRef.current) leftBgRef.current.style.opacity = "0";
			if (rightIconRef.current) {
				const iconPos = Math.min(absDx - ICON_OFFSET, halfWidth);
				rightIconRef.current.style.transform = `translateX(-${Math.max(0, iconPos)}px)`;
				rightIconRef.current.style.opacity = absDx > ICON_OFFSET ? "1" : "0";
				rightIconRef.current.style.transition = "opacity 150ms ease";
			}
		}
	}, [onSwipeRight, onSwipeLeft]);

	const handleEnd = useCallback(() => {
		const container = containerRef.current;
		if (!container || locked.current !== "horizontal") {
			locked.current = null;
			return;
		}

		const width = container.offsetWidth;
		const dx = deltaX.current;
		const progress = Math.abs(dx) / width;

		if (progress > THRESHOLD) {
			if (dx > 0 && onSwipeRight) {
				if (canSwipeRight && !canSwipeRight()) {
					resetCard();
				} else {
					flyOut("right", onSwipeRight);
				}
			} else if (dx < 0 && onSwipeLeft) {
				flyOut("left", onSwipeLeft);
			} else {
				resetCard();
			}
		} else {
			resetCard();
		}

		locked.current = null;
	}, [onSwipeRight, onSwipeLeft, canSwipeRight, flyOut, resetCard]);

	const handleTouchMove = useCallback((e: React.TouchEvent) => {
		if (disabled) return;
		const touch = e.touches[0];
		handleMove(touch.clientX, touch.clientY);
	}, [disabled, handleMove]);

	const handleTouchEnd = useCallback(() => {
		if (disabled) return;
		handleEnd();
	}, [disabled, handleEnd]);

	return (
		<div ref={containerRef} className="relative">
			{/* Left background — export (swipe right reveals this) */}
			{onSwipeRight && (
				<div
					ref={leftBgRef}
					className="absolute inset-0 bg-primary flex items-center justify-start opacity-0"
					style={{ zIndex: 0 }}
				>
					<div ref={leftIconRef}>
						<Send className="size-6 text-white" />
					</div>
				</div>
			)}

			{/* Right background — delete (swipe left reveals this) */}
			{onSwipeLeft && (
				<div
					ref={rightBgRef}
					className="absolute inset-0 bg-destructive flex items-center justify-end opacity-0"
					style={{ zIndex: 0 }}
				>
					<div ref={rightIconRef}>
						<Trash2 className="size-6 text-white" />
					</div>
				</div>
			)}

			{/* Card — sits on top, translates on swipe */}
			<div
				ref={cardRef}
				className="relative bg-background"
				style={{ zIndex: 1, touchAction: "pan-y" }}
				onTouchStart={handleTouchStart}
				onTouchMove={handleTouchMove}
				onTouchEnd={handleTouchEnd}
				onMouseDown={handleMouseDown}
			>
				{children}
			</div>
		</div>
	);
}
